import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { MarkersOfHarmState } from 'src/components/MarkersOfHarm/MarkersOfHarm.state';
import { Button } from 'src/domains/layouts/shared/buttons/Button';
import { Heading } from 'src/components/DepositLimitTab/NetDepositLimit/NetDepositLimit.style';
import { DateTime } from 'src_common/utils/time/time';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { useCommon } from 'src/AppState/Common';
import { Input } from 'src/domains/common/components/input/input';
import {
    MarkersOfHarmWrapper,
    InteractionWrapper,
    InteractionTypeWrapper,
    EmptyInteractionWrapper,
    DateInputWrapper,
    NextReviewDateLabel,
    InputNoteWrapper,
    InputInlineWrapper,
    ReasonWrapper,
    RiskWrapper,
    StatusWrapper,
    ProfitLossWrapper,
    ButtonsWrapper,
    ButtonWrapper,
    TableRow,
    DataTimeInputWrapper,
    NoMarkersInfo,
} from 'src/components/MarkersOfHarm/MarkersOfHarm.style';
import { Select } from 'src/domains/common/components/select/select';
import { CurrencyType } from 'src_common/common/amount/website-money/currency';

interface MarkersOfHarmPropsType {
    customerId: number;
    currency: CurrencyType;
}

export const MarkersOfHarm = observer('MarkersOfHarm', ({ customerId, currency }: MarkersOfHarmPropsType) => {
    const common = useCommon();
    const configState = ConfigState.get(common);
    const [state] = useState(() => new MarkersOfHarmState(() => customerId, configState, common));

    const renderInteractionForm = (): JSX.Element => (
        <InteractionWrapper>
            <InteractionTypeWrapper>
                <Select
                    label='Interaction'
                    placeholder='Select'
                    formInputState={state.selectedInteractionType}
                    values={state.interactionTypeOptions}
                    errors={[]}
                    dataTest='dropdown-interaction-type'
                />
            </InteractionTypeWrapper>
            <DateInputWrapper type='headLine'>
                <NextReviewDateLabel>Next Review Date</NextReviewDateLabel>{' '}
                <DataTimeInputWrapper
                    value={state.nextReviewTimestamp.value}
                    onChange={state.onChangeDate}
                    dateFormat='YYYY-MM-dd'
                    timeFormat='HH:mm'
                />
            </DateInputWrapper>
            <InputNoteWrapper>
                <Input
                    formInputState={state.note}
                    errors={[]}
                    label='Note'
                    placeholder='Note'
                    dataTest='input-note'
                />
            </InputNoteWrapper>
            <ReasonWrapper>
                <Select
                    label='Reason'
                    placeholder='Select'
                    formInputState={state.selectedReason}
                    values={state.reasonOptions}
                    errors={[]}
                    dataTest='dropdown-reason'
                />
            </ReasonWrapper>
            <RiskWrapper>
                <Select
                    label='Risk level'
                    placeholder='Select'
                    formInputState={state.selectedRiskLevel}
                    values={state.riskLevelOptions}
                    errors={[]}
                    dataTest='dropdown-risk'
                />
            </RiskWrapper>
            <StatusWrapper>
                <Select
                    label='Status'
                    placeholder='Select'
                    formInputState={state.interactionStatus}
                    values={state.statusOptions}
                    errors={[]}
                    dataTest='dropdown-status'
                />
            </StatusWrapper>
            <ProfitLossWrapper>
                <Input
                    label='Profit loss'
                    placeholder='Profit loss'
                    formInputState={state.profitLoss}
                    errors={[]}
                    dataTest='input-profit-loss'
                />
            </ProfitLossWrapper>
            <ButtonWrapper className='col-1'>
                <Button
                    size='regular'
                    onClick={() => state.onAddInteraction()}
                    dataTest='button-add-interaction'
                >
                    Add
                </Button>
            </ButtonWrapper>
        </InteractionWrapper>
    );

    return (
        <MarkersOfHarmWrapper>
            <div className='row'>
                <div className='col-12'>
                    <Heading data-test='markers-of-harm-heading'>Interactions</Heading>
                </div>
            </div>
            {state.editingIndex === -1 ? renderInteractionForm() : <EmptyInteractionWrapper />}
            <table className='table'>
                <thead>
                    <tr>
                        <th>Interaction Type</th>
                        <th>Date & Time</th>
                        <th>Next Review</th>
                        <th>Note</th>
                        <th>Reason</th>
                        <th>Risk Level</th>
                        <th>Status</th>
                        <th>Lifetime Profit/Loss</th>
                        <th>Agent</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {state.sortedInteractions.length > 0 ? (
                        state.sortedInteractions.map((interaction, index) => (
                            <TableRow
                                key={interaction.mohId}
                                data-test={`markers-of-harm-interaction-row-${index}`}
                            >
                                {state.editingIndex === interaction.mohId ? (
                                    <>
                                        <td>
                                            <Select
                                                placeholder='Select'
                                                formInputState={state.selectedInteractionType}
                                                values={state.interactionTypeOptions}
                                                errors={[]}
                                                dataTest='row-edit-dropdown-interaction-type'
                                            />
                                        </td>
                                        <td>
                                            {DateTime.from(interaction.interactionTimestamp)?.format(
                                                'YYYY-MM-DD HH:mm'
                                            )}
                                        </td>
                                        <td>
                                            <DateInputWrapper type='inline'>
                                                <DataTimeInputWrapper
                                                    value={state.nextReviewTimestamp.value}
                                                    onChange={state.onChangeDate}
                                                    dateFormat='YYYY-MM-dd'
                                                    timeFormat='HH:mm'
                                                    dataTest='row-edit-next-review-timestamp'
                                                />
                                            </DateInputWrapper>
                                        </td>
                                        <td>
                                            <InputInlineWrapper
                                                formInputState={state.note}
                                                errors={[]}
                                                placeholder='Note'
                                                dataTest='row-edit-input-note'
                                            />
                                        </td>
                                        <td>
                                            <Select
                                                placeholder='Select'
                                                formInputState={state.selectedReason}
                                                values={state.reasonOptions}
                                                errors={[]}
                                                dataTest='row-edit-dropdown-reason'
                                            />
                                        </td>
                                        <td>
                                            <Select
                                                placeholder='Select'
                                                formInputState={state.selectedRiskLevel}
                                                values={state.riskLevelOptions}
                                                errors={[]}
                                                dataTest='row-edit-dropdown-risk'
                                            />
                                        </td>
                                        <td>
                                            <Select
                                                placeholder='Select'
                                                formInputState={state.interactionStatus}
                                                values={state.statusOptions}
                                                errors={[]}
                                                dataTest='row-edit-dropdown-status'
                                            />
                                        </td>
                                        <td data-test='row-profit-loss'>
                                            {interaction.profitLoss === undefined
                                                ? '--'
                                                : interaction.profitLoss.format(currency)}
                                        </td>
                                        <td>{interaction.agent}</td>
                                        <td>
                                            <ButtonsWrapper>
                                                <Button
                                                    size='small'
                                                    onClick={() => state.onUpdateInteraction(interaction.mohId)}
                                                    dataTest='button-update-interaction'
                                                >
                                                    Update
                                                </Button>

                                                <Button
                                                    size='small'
                                                    onClick={state.onCancelEdit}
                                                    dataTest='button-cancel-edit'
                                                >
                                                    Cancel
                                                </Button>
                                            </ButtonsWrapper>
                                        </td>
                                    </>
                                ) : (
                                    <>
                                        <td data-test='row-interaction-type'>{interaction.labels.interactionType}</td>
                                        <td data-test='row-interaction-timestamp'>
                                            {DateTime.from(interaction.interactionTimestamp)?.format(
                                                'YYYY-MM-DD HH:mm'
                                            )}
                                        </td>
                                        <td data-test='row-next-review-timestamp'>
                                            {interaction.nextReviewTimestamp === undefined
                                                ? '--'
                                                : DateTime.from(interaction.nextReviewTimestamp)?.format(
                                                      'YYYY-MM-DD HH:mm'
                                                  )}
                                        </td>
                                        <td data-test='row-input-note'>{interaction.note}</td>
                                        <td data-test='row-reason'>{interaction.labels.reason}</td>
                                        <td data-test='row-risk'>{interaction.labels.riskLevel}</td>
                                        <td data-test='row-status'>{interaction.labels.interactionStatus}</td>
                                        <td data-test='row-profit-loss'>
                                            {interaction.profitLoss === undefined
                                                ? '--'
                                                : interaction.profitLoss.format(currency)}
                                        </td>
                                        <td data-test='row-agent'>{interaction.agent}</td>
                                        <td>
                                            <ButtonsWrapper>
                                                <Button
                                                    size='small'
                                                    onClick={() => state.onEditInteraction(interaction.mohId)}
                                                    dataTest={`button-edit-interaction-${index}`}
                                                >
                                                    Edit
                                                </Button>
                                                <Button
                                                    size='small'
                                                    status='delete'
                                                    onClick={() => state.onRemoveInteraction(interaction.mohId)}
                                                    dataTest={`button-remove-interaction-${index}`}
                                                >
                                                    Remove
                                                </Button>
                                            </ButtonsWrapper>
                                        </td>
                                    </>
                                )}
                            </TableRow>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={10}>
                                <NoMarkersInfo>No Markers created yet.</NoMarkersInfo>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </MarkersOfHarmWrapper>
    );
});
